"use client";
import React, {
  useState,
  createContext,
  useEffect,
  useRef,
  useContext,
} from "react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import Button from "../primitives/button";
import Icon from "../primitives/icon";
import RecentPick, { RecentPickState } from "../bettor/recent-pick";
import {
  BettorInfo,
  BettorProfileModel,
  FormattedOdd,
  GameAlignmentType,
  LineType,
  OddOutcome,
  PickStatus,
  PickType,
  RecentPick as RecentPickType,
  TeamBasic,
  changePickAlignment as changePickAlignmentInternal,
  determineUnitRiskGain,
  formatLineType,
  isSameLineType,
} from "common";
import { LoadingCircle } from "../primitives/loading";
import { usePathname } from "next/navigation";
import Link from "next/link";
import * as Dialog from "@radix-ui/react-dialog";
import TeamLogo from "../team/logo";
import { formatOdd } from "@/utils";
import Timestamp from "../game/timestamp";
import Info from "../primitives/info";
import Select from "../primitives/select";
import { AuthContext } from "@/app/(public)/drawer";
import { useOddsFormat } from "@/utils/providers/odds-format";
import { useScoreboard } from "@/utils/providers/scoreboard";
import { createProfileSlug } from "@/utils/url";
import { useRouter } from "next-nprogress-bar";
import { useUser } from "@clerk/nextjs";
import { useAuth } from "@clerk/clerk-react";
import mlClient from "@/ml-client";
import PickCheckout from "./checkout";
import PickSportsbooks, { getSportsbookEvents } from "./sportsbooks";
import CreatedPick from "./created";
import {
  PICK_PROFILE_UNAVAILABLE,
  PICK_UNEXPECTED_ERROR,
  pickErrorMessage,
} from "./pick-error";
import { useBettor } from "@/utils/providers/bettor";
import PickSlipTriggerContainer from "./trigger-container";
import BecomeASeller, { BecomeASellerType } from "../global/become-a-seller";
import { PickProvider } from "@/utils/providers/pick";

export interface PickSlipContextType {
  addPick: (
    pick: RecentPickType,
    pickAnimationElement: any | null,
    checkout: boolean
  ) => Promise<boolean>;
  getPickStatus: (pick: RecentPickType) => PickStatus;
  changePickAlignment: (pick: RecentPickType) => void;
  pickSlipPicks: RecentPickType[];
  createdPicks: RecentPickType[];
  justPurchasedPicks: { [key: number]: { type: PickType } } | null;
  setJustPurchasedPicks: React.Dispatch<
    React.SetStateAction<{ [key: number]: { type: PickType } } | null>
  >;
  checkoutPick: RecentPickType | null;
  setCheckoutPick: React.Dispatch<React.SetStateAction<RecentPickType | null>>;
  removeCheckoutPick: () => void;
  removePick: (pick: RecentPickType) => void;
  slipOpen: boolean;
  setSlipOpen: React.Dispatch<React.SetStateAction<boolean>>;
  slipState: string;
  setSlipState: React.Dispatch<React.SetStateAction<string>>;
  price: string;
  setPrice: React.Dispatch<React.SetStateAction<string>>;
  button: JSX.Element | null;
  setButton: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
  sportsbooks: Record<
    string,
    { title: string; url: string; logoUrl: string }[]
  > | null;
  setSportsbooks: React.Dispatch<
    React.SetStateAction<Record<
      string,
      { title: string; url: string; logoUrl: string }[]
    > | null>
  >;
}
export const PickSlipContext = createContext<PickSlipContextType | null>(null);
const RecentPickMemo = React.memo(RecentPick);

export const usePickSlip = () => {
  const context = useContext(PickSlipContext);
  if (!context) {
    throw new Error("usePickSlip must be used within a PickSlipProvider");
  }
  return context;
};

// type BettorPick = BettorPickSlipPick & BettorRecentPick;

export default function CreatePickProvider({
  children,
}: // bettor,
{
  children: React.ReactNode;
  // bettor: BettorProfileModel | null;
}) {
  const { bettor } = useBettor();
  const { getToken } = useAuth();
  const pathname = usePathname();
  const { setAuth } = useContext(AuthContext);

  const { isLoaded, isSignedIn, user } = useUser();
  const pickSlipTrigger = useRef<HTMLButtonElement>(null);

  const [slipOpen, setSlipOpen] = useState<boolean>(false);
  const [slipState, setSlipState] = useState<
    "checkout" | "create" | "loading" | "created"
  >("create");
  const [error, setError] = useState<string | undefined>();

  const { games } = useScoreboard();
  const [localPicks, setLocalPicks] = useState<RecentPickType[]>([]);
  const [checkoutPick, setCheckoutPick] = useState<RecentPickType | null>(null);
  const [createdPicks, setCreatedPicks] = useState<RecentPickType[]>([]);
  const [justCreatedPicks, setJustCreatedPicks] = useState<RecentPickType[]>(
    []
  );
  const [justPurchasedPicks, setJustPurchasedPicks] = useState<{
    [key: number]: { type: PickType };
  } | null>(null);
  const [picksInitialized, setPicksInitialized] = useState<boolean>(false);
  const [activePickIndex, setActivePickIndex] = useState<number>(0);
  const activePick = justCreatedPicks[activePickIndex];

  const [wageredUnits, setWageredUnits] = useState<number>();
  const [unitDiff, setUnitDiff] = useState<number>();

  const [sportsbooks, setSportsbooks] = useState<Record<
    string,
    { title: string; url: string; logoUrl: string }[]
  > | null>(null);

  const [button, setButton] = useState<JSX.Element | null>(null);
  const [price, setPrice] = useState<string>((0).toFixed(2));

  const handleButtonChange = React.useCallback((el: JSX.Element) => {
    setButton(el);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    const storedPicks = localStorage.getItem("picks");
    const storedCheckoutPick = localStorage.getItem("checkoutPick");
    const now = new Date();
    if (storedPicks) {
      setLocalPicks(
        JSON.parse(storedPicks)?.filter(
          (pick: RecentPickType) => new Date(pick.game.date_time_utc) > now
        )
      );
    }
    if (storedCheckoutPick) {
      const parsedCheckoutPick = JSON.parse(storedCheckoutPick);
      console.log("parsed checkout pick", parsedCheckoutPick);
      if (
        parsedCheckoutPick &&
        new Date(parsedCheckoutPick.game.date_time_utc) > now
      ) {
        setCheckoutPick(parsedCheckoutPick);
        setSlipState("checkout");
      } else {
        setCheckoutPick(null);
        localStorage.removeItem("checkoutPick");
        setSlipState("create");
      }
    }
    setPicksInitialized(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    localStorage.setItem("picks", JSON.stringify(localPicks));
    checkoutPick
      ? localStorage.setItem("checkoutPick", JSON.stringify(checkoutPick))
      : localStorage.removeItem("checkoutPick");
    console.log("Updated localStorage with picks:", localPicks);
    console.log("Updated localStorage with checkoutPick:", checkoutPick);
  }, [localPicks, checkoutPick]);

  useEffect(() => {
    if (games && localPicks.length > 0) {
      const now = new Date();
      // just for console logging number of deleted picks
      const removedPicks = localPicks.filter((pick) => {
        const pickIsInGameData = games.some(
          (game) => game.id === pick.game.global_game_id
        );
        if (!pickIsInGameData) return false;
        return !games.some(
          (game) =>
            game.id === pick.game.global_game_id && game.status === "Scheduled"
        );
      });

      setLocalPicks((prev) => {
        const next = prev.filter((pick) => {
          const pickIsInGameData = games.some(
            (game) => game.id === pick.game.global_game_id
          );
          if (!pickIsInGameData) return true;
          return games.some(
            (game) =>
              game.id === pick.game.global_game_id &&
              game.status === "Scheduled"
          );
        });
        // The scoreboard pushes a new `games` array every few seconds while
        // any game is live. Keep the same array when nothing was removed so
        // the slip does not re-render (and the wager effect does not re-run)
        // for an unchanged selection.
        return next.length === prev.length ? prev : next;
      });

      console.log("Removed picks: ", removedPicks.length);
    }
  }, [games]);

  /** Pick slip mounts or opens/closes */
  useEffect(() => {
    if (bettor) {
      setCreatedPicks(
        bettor.recentPicks && bettor.recentPicks.length > 0
          ? bettor.recentPicks
          : []
      );
      setJustCreatedPicks([]);
    }
  }, [isLoaded]);

  /** Picks change */
  useEffect(() => {
    if (
      localPicks.length === 0 &&
      checkoutPick === null &&
      justCreatedPicks.length === 0
    ) {
      setSlipOpen(false);
    }
  }, [localPicks.length, checkoutPick]);

  /** Route changes */
  useEffect(() => {
    slipOpen && setSlipOpen(false);
  }, [pathname]);

  /** Updates the wager */
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    if (justCreatedPicks.length > 0) return;

    // The submit error is cleared where the user changes the slip (addPick,
    // removePickByIndex, removeAll, removePick, changeAlignment) and at the
    // start of createPick, not here: this effect also runs for scoreboard
    // pushes and would hide a refused submit's message within seconds.
    let totalWageredUnits = 0;
    let totalUnitDiff = 0;

    localPicks.forEach((pick) => {
      if (pick) {
        const { risk, gain } = determineUnitRiskGain(pick);
        totalWageredUnits += risk;
        totalUnitDiff += gain;
      }
    });

    setWageredUnits(totalWageredUnits);
    setUnitDiff(totalUnitDiff);
    localStorage.setItem("picks", JSON.stringify(localPicks));
  }, [localPicks, createdPicks]);

  /** Open changes */
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    setJustCreatedPicks([]);
    // Only a successfully created slip is cleared on close. A failed submit
    // keeps its selections (see createPick) so the user can retry or remove.
    if (!slipOpen && slipState === "created") {
      setLocalPicks([]);
      setSlipState("create");
    }

    const body = document.body;
    if (slipOpen) {
      body.classList.add("remove-scroll");
    } else {
      body.classList.remove("remove-scroll");
    }
  }, [slipOpen]);

  useEffect(() => {
    console.log("pick slip state", slipState);
  }, [slipState]);

  async function addPick(
    pick: RecentPickType,
    pickAnimationElement?: any | null,
    checkout: boolean = false
  ): Promise<boolean> {
    setError(undefined);
    if (checkout) {
      setCheckoutPick(pick);
      setSlipState("checkout");
      setSlipOpen(true);
    } else {
      const conflictingPicks = [...localPicks, ...createdPicks].filter(
        (prevPick) => {
          const sameGame =
            prevPick.game.global_game_id === pick.game.global_game_id;
          if (sameGame) {
            if (prevPick.type === pick.type) {
              return true;
            }
          }
        }
      );

      if (conflictingPicks.length > 0) {
        // alert("You've already made a conflicting pick on this game.");
        return false;
      }

      pick &&
        setLocalPicks((prevPicks) => {
          const newPicks = [pick, ...prevPicks]; // Add pick to the start of the array
          return newPicks;
        });
    }

    if (pickAnimationElement) {
      await addPickAnimation(pickAnimationElement);
      setTimeout(() => {
        if (pickSlipTrigger.current) {
          const pickNumber = document.querySelector(".pick-slip-number")!;
          if (pickNumber) {
            pickNumber.classList.add("animate-pickNumberBounce");
          }
          pickSlipTrigger.current.classList.add("animate-pickSlipTrigger");
          setTimeout(() => {
            const pickNumber = document.querySelector(".pick-slip-number")!;
            if (pickNumber) {
              pickNumber?.classList.remove("animate-pickNumberBounce");
            }
            pickSlipTrigger.current?.classList.remove(
              "animate-pickSlipTrigger"
            );
          }, 500);
        }
      }, 50);
      setSlipState(checkout ? "checkout" : "create");
    }
    return true;
  }

  function changeAlignment(pickIndex: number) {
    setError(undefined);
    let pick = localPicks[pickIndex];
    changePickAlignmentInternal(pick);
    const newPick = { ...pick };
    setLocalPicks((prev) => {
      const newPicks = [...prev];
      newPicks[pickIndex] = newPick;
      return newPicks;
    });
  }

  function removePickByIndex(index: number) {
    setError(undefined);
    setLocalPicks((prev) => prev.filter((_, i) => i !== index));
  }

  function removeAll() {
    setError(undefined);
    setLocalPicks([]);
  }

  async function createPick() {
    setError(undefined);
    setSlipState("loading");

    if (!user) {
      setSlipState("create");
      setError("You must be signed in to make a pick");
      return;
    }
    if (!bettor) {
      // Signed in but the bettor profile did not load with the page: posting
      // would send [null] and the API answers with an internal 400.
      setSlipState("create");
      setError(PICK_PROFILE_UNAVAILABLE);
      return;
    }

    const picksPayload = localPicks.map((pick: RecentPickType) => {
      console.log("pick from payload", pick);
      return {
        global_game_id: pick.game.global_game_id,
        type: pick.type,
        sport_type: pick.sport_type,
        comment: pick.comment,
        sale_price: pick.sale_price,
      };
    });

    try {
      const token = await getToken();
      const res = await mlClient.post("/picks", picksPayload, token);
      if (!res.ok) {
        // Keep the selections: the user can remove the offending pick or
        // retry once odds are back. The API's message is shown inline.
        console.error("Error response:", res.code, res.message);
        setSlipState("create");
        setError(pickErrorMessage(res));
        return;
      }
      console.log("response from picks", res);
      setCreatedPicks((prevCreatedPicks) => [
        ...localPicks,
        ...prevCreatedPicks,
      ]);
      setLocalPicks([]);
      setJustCreatedPicks(localPicks);
      const sportsbooksEvents = await getSportsbookEvents(localPicks);
      setSportsbooks(sportsbooksEvents);
      setSlipState("created");
    } catch (err) {
      console.error("Error creating picks:", err);
      setSlipState("create");
      setError(PICK_UNEXPECTED_ERROR);
    } finally {
      // router.refresh();
    }
  }

  async function populateSportsbooks(picks: RecentPickType[]) {
    try {
      const gameIds = picks
        .map((pick: RecentPickType) => pick.game.global_game_id)
        .join(",");
      const request = `${process.env.NEXT_PUBLIC_API_URL}/SportsbookEvents?gameIds=${gameIds}`;
      const res = await fetch(request);
      if (!res.ok) {
        throw new Error("Failed to fetch sportsbooks");
      }
      const data = await res.json();
      console.log("sportsbooks:", data);
      setSportsbooks(data);
    } catch (error) {
      console.error("Error fetching sportsbooks:", error);
    }
  }

  const [initialTouchPosition, setInitialTouchPosition] = useState<
    number | null
  >(null);

  function changePickAlignment(pick: RecentPickType) {
    const matchingPick = [...localPicks, ...createdPicks].find((prevPick) => {
      const sameGame =
        prevPick.game.global_game_id === pick.game.global_game_id;
      if (sameGame) {
        if (
          prevPick.odds[prevPick.alignment].type ===
          pick.odds[pick.alignment].type
        ) {
          return true;
        }
      }
    });

    if (matchingPick) {
      changeAlignment(localPicks.indexOf(matchingPick));
    }
  }

  function getPickStatus(pick: RecentPickType): PickStatus {
    return {
      // This specific pick exists in the pick slip
      // Show green odd state
      added: localPicks.some((p) => {
        const sameGame = p.game.global_game_id === pick.game.global_game_id;
        if (sameGame) {
          if (p.type === pick.type) {
            return true;
          }
        }
      }),
      // This specific pick was created by the user already
      // Show the lock or checkmark, depending on the state of the game
      created: createdPicks.some((p) => {
        const sameGame = p.game.global_game_id === pick.game.global_game_id;
        if (sameGame) {
          if (p.type === pick.type) {
            return true;
          }
        }
      }),

      opposing: {
        // Already added a pick of the same line for this game.
        added: localPicks.some((p) => {
          const sameGame = p.game.global_game_id === pick.game.global_game_id;
          const sameLine = isSameLineType(p, pick);
          if (sameGame && sameLine) {
            return true;
          }
        }),
        // Already created a pick of the same line for this game.
        created: createdPicks.some((p) => {
          const sameGame = p.game.global_game_id === pick.game.global_game_id;
          const sameLine = isSameLineType(p, pick);
          if (sameGame && sameLine) {
            return true;
          }
        }),
      },

      game: {
        // game has a pick in the pick slip of any type
        added: localPicks.some(
          (p) => p.game.global_game_id === pick.game.global_game_id
        ),

        // Game has a pick of any type already made by the user
        created: createdPicks.some(
          (p) => p.game.global_game_id === pick.game.global_game_id
        ),
      },
    };
  }

  function removePick(pick: RecentPickType) {
    setError(undefined);
    setLocalPicks((prev) =>
      prev.filter((p) => {
        const sameGame = p.game.global_game_id === pick.game.global_game_id;
        if (sameGame) {
          if (p.type === pick.type) {
            return false;
          }
        }
        return true;
      })
    );
  }

  const removeCheckoutPick = () => {
    setCheckoutPick(null);
    localStorage.removeItem("checkoutPick");
    setSlipState("create");
  };

  function addPickAnimation(
    pickAnimationElement: React.RefObject<HTMLDivElement>
  ): Promise<boolean> {
    return new Promise((resolve) => {
      if (pickAnimationElement.current) {
        const clonedPick = pickAnimationElement.current.cloneNode(
          true
        ) as HTMLDivElement;
        const innerButton = clonedPick.querySelector("button")!;

        const pickNumber = document.querySelector(".pick-slip-number")!;

        clonedPick.style.position = "fixed";
        clonedPick.style.zIndex = "49";

        clonedPick.classList.add("cloned-pick");
        innerButton.classList.add("cloned-pick-inner");

        innerButton.style.backgroundColor = "#B1ED40";
        clonedPick.style.top =
          pickAnimationElement.current.getBoundingClientRect().top + "px";
        clonedPick.style.left =
          pickAnimationElement.current.getBoundingClientRect().left + "px";
        document.body.appendChild(clonedPick);

        const clonedPickPosition = {
          x: clonedPick.getBoundingClientRect().left,
          y: clonedPick.getBoundingClientRect().top,
        };

        const pickSlipNumberPosition = {
          x: pickNumber?.getBoundingClientRect().left!,
          y: pickNumber?.getBoundingClientRect().top!,
        };

        const mobileAdjustment = {
          x: window.innerWidth <= 550 ? 0 : 0,
          y: window.innerWidth <= 550 ? 50 : 0,
        };

        const transformDistanceX =
          pickSlipNumberPosition.x -
          clonedPickPosition.x -
          24 +
          mobileAdjustment.x;
        const transformDistanceY =
          pickSlipNumberPosition.y - clonedPickPosition.y + mobileAdjustment.y;

        clonedPick.style.transform = "translateX(" + transformDistanceX + "px)";
        innerButton.style.transform =
          "translateY(" + transformDistanceY + "px)";

        setTimeout(() => {
          clonedPick.style.opacity = "0";
          clonedPick.style.transform = "";
          innerButton.style.transform = "";
          document.body.removeChild(clonedPick);
        }, 750);

        setTimeout(() => {
          resolve(true);
        }, 700);
      }
    });
  }

  return (
    <PickSlipContext.Provider
      value={{
        addPick,
        getPickStatus,
        changePickAlignment,
        pickSlipPicks: localPicks,
        createdPicks: createdPicks,
        checkoutPick,
        slipOpen,
        setSlipOpen,
        slipState,
        setSlipState,
        justPurchasedPicks,
        setJustPurchasedPicks,
        setCheckoutPick,
        removeCheckoutPick,
        removePick,
        price,
        setPrice,
        button,
        setButton,
        sportsbooks,
        setSportsbooks,
      }}
    >
      <Dialog.Root
        open={slipOpen}
        onOpenChange={(value) => setSlipOpen(value)}
        modal={true}
      >
        <PickSlipTriggerContainer>
          <Dialog.Trigger
            ref={pickSlipTrigger}
            className={`create-pick-trigger fixed z-[40] -bottom-[10px] right-0 max-[550px]:left-0 max-[550px]:mx-auto lg:right-16 max-w-[calc(100vw-16px)] w-[calc(100vw-16px)] min-[550px]:w-[430px] origin-bottom transition-[opacity_0.3s,transform_0.3s] h-[94px] hover:translate-y-[-5px] focus-visible:translate-y-[-5px] group ${
              localPicks.length > 0 || checkoutPick !== null
                ? " opacity-100 pointer-events-auto"
                : " opacity-0 pointer-events-none"
            } focus-visible:[outline:none]`}
          >
            <PickSlipTrigger />
          </Dialog.Trigger>
        </PickSlipTriggerContainer>

        <Dialog.Portal>
          <Dialog.Overlay className="z-[50] fixed inset-0 bg-black bg-opacity-80 data-[state=closed]:animate-overlayHide data-[state=open]:animate-overlayShow" />
          <Dialog.Content
            onEscapeKeyDown={(e) => {
              if (slipState === "checkout") {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              if (slipState === "checkout") {
                e.preventDefault();
              }
            }}
            className="right-0 lg:right-16 max-[550px]:left-0 max-[550px]:mx-auto bottom-0 z-[51] fixed grid grid-rows-[min-content_auto_min-content] w-[calc(100vw-16px)] min-[550px]:w-[430px] max-w-[calc(100vw-16px)] h-[85dvh] lg:h-[90vh] max-h-[100dvh] lg:max-h-[90vh] data-[state=closed]:animate-pickSlipSlideOut data-[state=open]:animate-pickSlipSlideIn"
          >
            {slipState === "checkout" ? (
              <PickSlipTrigger />
            ) : (
              <Dialog.Close className="focus:outline-none ![outline:none] focus-visible:outline-none group">
                <PickSlipTrigger />
              </Dialog.Close>
            )}
            <div className="flex flex-col justify-between gap-2 bg-surface-600 min-h-0 grow">
              <ScrollArea.Root className="h-full">
                <ScrollArea.Viewport
                  id="pick-slip-scrollarea-viewport"
                  className="w-full h-full bg-surface-600"
                >
                  {/* <div className="flex flex-col justify-between bg-surface-600 overflow-y-auto grow"> */}
                  {slipState === "checkout" && checkoutPick ? (
                    <div className="flex flex-col p-4">
                      <PickCheckout />
                    </div>
                  ) : slipState === "loading" ? (
                    <div className="flex flex-col justify-between bg-surface-600">
                      <div className="px-3 py-4">
                        {/* PICK CARD */}
                        <div className="flex flex-col justify-center items-center gap-6 bg-surface-700 px-4 py-3 rounded-xl">
                          <LoadingCircle />
                          <div className="text-display text-secondary-300 uppercase">
                            Creating Pick...
                          </div>
                          <div className="text-body-regular text-secondary-500">
                            Good luck!
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : slipState === "create" ? (
                    <div className={`py-4 px-3 flex flex-col gap-3`}>
                      {error && (
                        <Info.Inline
                          title="Error"
                          message={error}
                          variant="error"
                          filled
                          action={
                            isSignedIn && localPicks.length > 0
                              ? { label: "Retry", onClick: () => createPick() }
                              : undefined
                          }
                        />
                      )}

                      {localPicks.length ? (
                        <>
                          <BecomeASeller type={BecomeASellerType.PickSlip} />
                          {localPicks.map((pick, i) => (
                            <PickProvider
                              pick={pick}
                              key={`${pick.game.global_game_id}-${pick.type}`}
                            >
                              <RecentPickMemo
                                state={
                                  checkoutPick === null
                                    ? RecentPickState.Create
                                    : RecentPickState.Checkout
                                }
                                updateSalePrice={(salePrice) => {
                                  setLocalPicks((prev) => {
                                    const newPicks = [...prev];
                                    newPicks[i].sale_price = salePrice;
                                    return newPicks;
                                  });
                                }}
                                updateComment={(comment) => {
                                  setLocalPicks((prev) => {
                                    const newPicks = [...prev];
                                    newPicks[i].comment = comment;
                                    return newPicks;
                                  });
                                }}
                                changeAlignment={() => changeAlignment(i)}
                                remove={() => removePickByIndex(i)}
                                isSeller={true}
                              />
                            </PickProvider>
                          ))}
                          <div>
                            {localPicks.length > 1 && (
                              <div className="flex justify-center pb-8">
                                <Button
                                  onClick={removeAll}
                                  // className="flex items-center gap-1 text-body-sm-medium text-secondary-500 uppercase focus:outline-none whitespace-nowrap"
                                  className="bg-surface-700 !hover:bg-error-600"
                                  variant="standard"
                                  color="tertiary"
                                >
                                  Remove All Picks{" "}
                                  <Icon
                                    name="bin"
                                    className="fill-secondary-500"
                                    size="sm"
                                  />
                                </Button>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="px-3 py-4">
                          {/* PICK CARD */}
                          <div className="flex flex-col justify-center items-center gap-6 bg-surface-700 px-4 py-3 rounded-xl">
                            <div className="text-display text-secondary-300 uppercase">
                              No picks
                            </div>
                            <div className="text-body-regular text-secondary-500">
                              Click on a pick to add it to your slip
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : slipState === "created" ? (
                    <>
                      {justCreatedPicks.length > 1 && (
                        <CreatedPicksSelector
                          picks={justCreatedPicks}
                          activePickIndex={activePickIndex}
                          setPick={(index) => {
                            setActivePickIndex(index);
                          }}
                        />
                      )}
                      <div className="gap-6 grid grid-rows-[auto_auto] px-2 sm:px-3 pb-4 overflow-y-auto grow mt-2">
                        <div className="flex flex-col gap-4">
                          <CreatedPick pick={activePick} />
                        </div>
                        <PickSportsbooks
                          sportsbooks={sportsbooks}
                          activePick={activePick}
                        />
                      </div>
                    </>
                  ) : null}
                </ScrollArea.Viewport>
                <ScrollArea.Scrollbar className="flex select-none touch-none p-0.5 bg-transparent desktop-grid:bg-secondary-700 transition-colors duration-[160ms] ease-out hover:bg-blackA5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-2.5">
                  <ScrollArea.Thumb className="flex-1 bg-secondary rounded-[10px] relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-full before:h-full before:min-w-[44px] before:min-h-[44px] z-0 desktop-grid:z-[50]" />
                </ScrollArea.Scrollbar>
              </ScrollArea.Root>
            </div>
            <div className="bg-secondary-300 w-full min-h-[76px]">
              {/* <div className="flex bg-secondary-400 w-full h-12 text-white">test</div> */}
              {slipState === "create" || slipState === "loading" ? (
                isLoaded && isSignedIn ? (
                  <div
                    className={`flex justify-between items-center p-4 ${
                      slipState === "loading"
                        ? "opacity-50 pointer-events-none"
                        : ""
                    }`}
                  >
                    <div className="flex gap-6">
                      <div className="flex flex-col gap-[2px]">
                        <div className="text-secondary-600 text-title-medium uppercase">
                          Total Risk
                        </div>
                        <div className="text-headline-bold text-surface-700">
                          {wageredUnits}
                          <span className="text-title-lg-medium"> Units</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-[2px]">
                        <div className="text-secondary-600 text-title-medium uppercase">
                          To Win
                        </div>
                        <div className="text-headline-bold text-surface-700">
                          {unitDiff}
                          <span className="text-title-lg-medium"> Units</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Button
                        onClick={() => createPick()}
                        disabled={slipState === "loading"}
                        className="!justify-center min-w-[146px]"
                      >
                        {localPicks.length > 1 ? "Submit Picks" : "Submit Pick"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center gap-10 py-4">
                    <div className="text-secondary-600 text-label-medium uppercase">
                      Login or create an account to submit picks
                    </div>
                    <div>
                      <Button
                        onClick={() => {
                          setSlipOpen(false);
                          setAuth("register");
                        }}
                      >
                        Login or Create Account
                      </Button>
                    </div>
                  </div>
                )
              ) : slipState === "created" ? (
                <div className="flex justify-between items-center p-4">
                  <div className="flex gap-6">
                    <div className="flex flex-col gap-[2px]">
                      <div className="text-secondary-600 text-title-medium uppercase">
                        Total Risk
                      </div>
                      <div className="text-headline-bold text-surface-700">
                        {wageredUnits}
                        <span className="text-title-lg-medium"> Units</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-[2px]">
                      <div className="text-secondary-600 text-title-medium uppercase">
                        To Win
                      </div>
                      <div className="text-headline-bold text-surface-700">
                        {unitDiff}
                        <span className="text-title-lg-medium"> Units</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Button
                      onClick={() => setSlipOpen(false)}
                      className="!justify-center min-w-[146px]"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              ) : (
                slipState === "checkout" && (
                  <div className="flex justify-between items-center p-4 h-full">
                    <div className="flex gap-6">
                      <div className="flex flex-row items-center flex-start">
                        <div className="text-headline-bold text-surface-700">
                          USD ${price}
                        </div>
                      </div>
                    </div>
                    <div>
                      {/* <Button
                      onClick={() => setSlipOpen(false)}
                      className="!justify-center min-w-[146px]"
                    >
                      Done
                    </Button> */}
                      {button}
                    </div>
                  </div>
                )
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {children}
    </PickSlipContext.Provider>
  );
}

// function Pick({
//   pick,
//   num,
//   updateComment,
//   changeAlignment,
//   remove,
// }: {
//   pick: BettorPick;
//   num: number;
//   updateComment: (comment: string | undefined) => void;
//   changeAlignment: () => void;
//   remove: () => void;
// }) {
//   // const [sellPick, setSellPick] = useState(false);
//   const [comment, setComment] = useState(pick.comment || "");
//   const [addComment, setAddComment] = useState(false);

//   const toggleComment = () => {
//     if (!addComment) {
//       setAddComment(true);
//       updateComment(comment);
//     } else {
//       setAddComment(false);
//       updateComment(undefined);
//     }
//   };

//   useEffect(() => {
//     updateComment(comment);
//   }, [comment]);

//   return (
//     <div className="flex flex-col gap-1 bg-surface-700 px-4 pt-1 pb-[6px] rounded-xl">
//       <div className="flex justify-between items-center">
//         <Link
//           href={createGameDetailSlug(pick.game.league, pick.game.id)}
//           className="mt-1 line-clamp-1 text-secondary-300 text-title-sm-bold uppercase overflow-ellipsis flex gap-1 items-center"
//         >
//           <span>{pick.game.awayTeam.name}</span>{" "}
//           <span className="text-label-sm-bold">@</span>{" "}
//           <span>{pick.game.homeTeam.name}</span>
//         </Link>
//         <div className="flex flex-row gap-4 items-center">
//           <button
//             onClick={toggleComment}
//             className="flex items-center gap-1 text-body-sm-medium text-secondary-500 uppercase focus:outline-none"
//           >
//             <Icon
//               name="chat-bubble"
//               className={`${
//                 addComment ? "text-secondary-300" : "text-secondary-500"
//               }`}
//               size="sm"
//             />
//           </button>
//           <button
//             onClick={remove}
//             className="flex items-center gap-1 text-body-sm-medium text-secondary-500 uppercase focus:outline-none"
//           >
//             <Icon name="bin" className="fill-secondary-500" size="sm" />
//           </button>
//         </div>
//       </div>

//       <div>
//         <RecentPick
//           pick={pick as BettorRecentPick}
//           changeAlignment={changeAlignment}
//           lockedIn={false}
//         />
//       </div>
//       {addComment && (
//         <div className="flex flex-col gap-4">
//           {/* <div className="flex justify-between items-center">
//           <div className="text-body-regular text-secondary-300">
//             Add Comment
//           </div>
//           <div>
//             <Switch onCheckedChange={toggleComment} />
//           </div>
//         </div> */}
//           <Form>
//             <Form.Field name="comment" className="flex-1">
//               <Form.Input
//                 type="text"
//                 placeholder="Write a comment..."
//                 value={comment}
//                 onChange={(e) => setComment(e.target.value)}
//               >
//                 Comment
//               </Form.Input>
//             </Form.Field>
//           </Form>
//           {/* <div className="flex justify-between items-center">
//           <div className="text-body-regular text-secondary-300">Sell Pick</div>
//           <div>
//             <Switch onCheckedChange={(checked) => setSellPick(checked)} />
//           </div>
//         </div>
//         {sellPick && <img src="/placeholder/sell-pick.svg" />} */}
//         </div>
//       )}
//     </div>
//   );
// }

function CreatedPicksSelector({
  picks,
  activePickIndex,
  setPick,
}: {
  picks: RecentPickType[];
  activePickIndex: number;
  setPick: (index: number) => void;
}) {
  return (
    <div className="flex px-3 mt-2 pr-[18px]">
      <Select
        options={picks.map((pick, i) => ({
          label: <PickOptionComponent pick={pick} />,
          value: i.toString(),
        }))}
        label="Select a pick"
        value={activePickIndex.toString()}
        onValueChange={(value) => setPick(parseInt(value))}
        variant="pick"
        height={16}
      />
    </div>
  );
}

function PickOptionComponent({ pick }: { pick: RecentPickType }) {
  const { oddsFormat } = useOddsFormat();

  const awayTeam = pick.game.teams[0];

  const homeTeam = pick.game.teams[1];

  const odds = pick.odds[pick.alignment];
  if (!odds) {
    console.error("Odds are undefined for alignment", pick.alignment);
    return null;
  }
  const isTotal = odds.type === LineType.Total;
  const formattedOdd = formatOdd(oddsFormat, odds)!;
  return (
    <div className="flex justify-between items-center gap-3 w-full normal-case whitespace-nowrap">
      <div className="flex items-center gap-[6px] text-title-sm-medium">
        <div className="flex items-center">
          {awayTeam.abbreviation}
          <TeamLogo team={awayTeam} size="xs" className="p-1" />
        </div>
        <div className="text-title">@</div>
        <div className="flex items-center">
          <TeamLogo team={homeTeam} size="xs" className="p-1" />
          {homeTeam.abbreviation}
        </div>
      </div>
      <div className="bg-primary px-4 py-[6px] rounded-3xl text-secondary-300 text-title-lg-medium text">
        <span className="capitalize">{formatLineType(odds.type)}</span>
        <span> • </span>
        <span>
          {isTotal
            ? ""
            : pick.alignment === GameAlignmentType.Away
            ? awayTeam.abbreviation
            : homeTeam.abbreviation}
        </span>{" "}
        <span>
          {odds.type === LineType.Moneyline
            ? formattedOdd.payout
            : formattedOdd.value}
        </span>
      </div>
    </div>
  );
}

function SportsbookCard({
  sportsbook,
  odd,
}: {
  sportsbook: any;
  odd: FormattedOdd;
}) {
  return (
    <div className="flex flex-col items-center gap-[10px] bg-surface-700 px-4 py-4 rounded-md">
      <img
        src={sportsbook.logoUrl}
        alt={sportsbook.title}
        className="w-auto h-10"
      />
      <div className="text-center">
        <div className="line-clamp-2 min-h-[28px] text-secondary-400 text-title-sm-medium">
          {sportsbook.title}
        </div>
        {/* <div className="text-secondary-400 text-title-sm-medium">
          <span className="text-secondary-300">{props.odd.value}</span>{" "}
          {props.odd.payout}
        </div> */}
      </div>

      <a
        className="btn btn-sm btn-filled-primary"
        href={sportsbook.url}
        target="_blank"
      >
        Bet Now
      </a>
    </div>
  );
}

interface FeaturedSportsbookProps {
  sportsbook: {
    attributes: {
      title: string;
      slug: string;
      url: string;
      thumbnail: {
        data: {
          attributes: {
            url: string;
          };
        };
      };
    };
  };
  odd: {
    value: string;
    payout?: string | undefined;
    outcome?: OddOutcome | undefined;
  };
  unitDiff: number;
}

function FeaturedSportsbook(props: FeaturedSportsbookProps) {
  return (
    <div className="flex justify-between items-center gap-6 bg-secondary-300 p-4 rounded-xl">
      <div className="flex items-center gap-4">
        <div>
          <img
            src={props.sportsbook.attributes.thumbnail.data.attributes.url}
            alt={props.sportsbook.attributes.title}
            className="w-auto h-10"
          />
        </div>
        <div className="flex flex-col text-surface-700 text-title-lg-medium">
          <span>{props.sportsbook.attributes.title}</span>
          {/* <span>
          <span className="text-surface-700">{props.odd.value}</span>{" "}
          <span>{props.odd.payout}</span>
        </span>
        <span className="text-surface-700">To Win: {props.unitDiff}</span> */}
        </div>
      </div>
      <div>
        <Button
          href={props.sportsbook.attributes.url}
          target="_blank"
          size="xs"
        >
          Bet Now
        </Button>
      </div>
    </div>
  );
}

function PickSlipTrigger() {
  const slip = usePickSlip();
  const [initialTouchPosition, setInitialTouchPosition] = useState<
    number | null
  >(null);

  const [displayedNumber, setDisplayedNumber] = useState<number>(
    slip.pickSlipPicks.length
  );

  useEffect(() => {
    if (displayedNumber !== slip.pickSlipPicks.length) {
      setTimeout(
        () => {
          setDisplayedNumber(slip.pickSlipPicks.length);
        },
        slip.pickSlipPicks.length > displayedNumber ? 700 : 0
      );
    }
  }, [slip.pickSlipPicks.length]);

  return (
    <div
      className={`relative w-full flex justify-between items-center px-[28px] py-[24px] ${
        slip.slipOpen ? "" : "pb-[34px]"
      } rounded-t-xl `}
      onDragStart={(e) => {
        e.dataTransfer.dropEffect = "move";
        slip.setSlipOpen(!slip.slipOpen);
      }}
      onTouchStart={(e) => {
        setInitialTouchPosition(e.touches[0].clientY);
      }}
      onTouchMove={(e) => {
        if (slip.slipOpen) {
          if (
            initialTouchPosition !== null &&
            e.touches[0].clientY > initialTouchPosition + 50
          ) {
            slip.setSlipOpen(false);
          }
        } else {
          if (
            initialTouchPosition !== null &&
            e.touches[0].clientY < initialTouchPosition + 50
          ) {
            slip.setSlipOpen(true);
          }
        }
      }}
      style={{
        filter: slip.slipOpen
          ? undefined
          : "drop-shadow(0 7px 40px rgba(24, 27, 34, 0.5))",
      }}
    >
      <div
        className={`absolute inset-0 bg-no-repeat bg-right pointer-events-none bg-[length:auto_100%] transition-colors ${
          ``
          // slipOpen ? "bg-secondary-300" : "bg-primary-500"
        }`}
        style={{
          backgroundImage: slip.slipOpen
            ? `url("data:image/svg+xml,%3Csvg width='266' height='84' viewBox='0 0 266 84' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M240.844 38.0744L268.789 -3.80902L259.223 -10.1917L227.019 38.0741L263.223 92.34L272.789 85.9578L240.844 38.0744Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M23.5004 38.097L58.8313 -4.85769L53.6182 -9.14556L14.7999 38.049L57.5948 91.2627L62.8548 87.0325L23.5004 38.097Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M176.947 38.0802L206.264 -4.44519L198.854 -9.55353L166.022 38.0703L202.85 91.6974L210.269 86.6025L176.947 38.0802Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M138.45 38.0862L169.69 -4.48765L162.837 -9.51621L127.927 38.0592L166.824 91.644L173.702 86.6507L138.45 38.0862Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M157.545 38.0851L187.873 -4.53457L180.947 -9.46272L147.126 38.0668L184.938 91.6025L191.881 86.6987L157.545 38.0851Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M119.99 38.0853L151.338 -4.63569L144.888 -9.36845L110.086 38.0598L148.876 91.497L155.35 86.7974L119.99 38.0853Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M103.995 38.0858L135.289 -4.56167L128.638 -9.44233L93.782 38.0595L132.625 91.5705L139.302 86.7241L103.995 38.0858Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M262.626 38.0669L289.774 -4.0206L280.53 -9.98316L249.529 38.0786L284.535 92.137L293.768 86.1578L262.626 38.0669Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M87.7426 38.0898L120.006 -4.58949L113.625 -9.4138L77.7393 38.0562L117.609 91.5383L124.023 86.7569L87.7426 38.0898Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M71.4895 38.0933L104.728 -4.62172L98.6116 -9.38116L61.7009 38.0531L102.592 91.5024L108.747 86.7933L71.4895 38.0933Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.51317 38.098L44.7795 -4.77818L39.3733 -9.22486L0.490385 38.0482L43.349 91.3412L48.8038 86.9543L9.51317 38.098Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M219.392 38.0744L247.43 -3.94778L238.28 -10.053L206.168 38.0741L242.279 92.2013L251.43 86.0965L219.392 38.0744Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M55.2809 38.0999L90.418 -4.61917L84.6257 -9.38348L45.6136 38.0467L88.5996 91.498L94.4441 86.7978L55.2809 38.0999Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M197.095 38.0802L226.413 -4.44519L219.003 -9.55353L186.171 38.0703L222.998 91.6974L230.417 86.6025L197.095 38.0802Z' fill='%23232528'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M38.81 38.098L74.0764 -4.77818L68.6702 -9.22486L29.7873 38.0482L72.6459 91.3412L78.1007 86.9543L38.81 38.098Z' fill='%23232528'/%3E%3C/svg%3E%0A"), linear-gradient(92.8deg, #222326 10.06%, #131517 97.89%)`
            : `url("data:image/svg+xml,%3Csvg width='266' height='84' viewBox='0 0 266 84' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M240.844 38.0744L268.789 -3.80902L259.223 -10.1917L227.019 38.0741L263.223 92.34L272.789 85.9578L240.844 38.0744Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M23.5004 38.097L58.8313 -4.85769L53.6182 -9.14556L14.7999 38.049L57.5948 91.2627L62.8548 87.0325L23.5004 38.097Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M176.947 38.0802L206.264 -4.44519L198.854 -9.55353L166.022 38.0703L202.85 91.6974L210.269 86.6025L176.947 38.0802Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M138.45 38.0862L169.69 -4.48765L162.837 -9.51621L127.927 38.0592L166.824 91.644L173.702 86.6507L138.45 38.0862Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M157.545 38.0851L187.873 -4.53457L180.947 -9.46272L147.126 38.0668L184.938 91.6025L191.881 86.6987L157.545 38.0851Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M119.99 38.0853L151.338 -4.63569L144.888 -9.36845L110.086 38.0598L148.876 91.497L155.35 86.7974L119.99 38.0853Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M103.995 38.0858L135.289 -4.56167L128.638 -9.44233L93.782 38.0595L132.625 91.5705L139.302 86.7241L103.995 38.0858Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M262.626 38.0669L289.774 -4.0206L280.53 -9.98316L249.529 38.0786L284.535 92.137L293.768 86.1578L262.626 38.0669Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M87.7426 38.0898L120.006 -4.58949L113.625 -9.4138L77.7393 38.0562L117.609 91.5383L124.023 86.7569L87.7426 38.0898Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M71.4895 38.0933L104.728 -4.62172L98.6116 -9.38116L61.7009 38.0531L102.592 91.5024L108.747 86.7933L71.4895 38.0933Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.51317 38.098L44.7795 -4.77818L39.3733 -9.22486L0.490385 38.0482L43.349 91.3412L48.8038 86.9543L9.51317 38.098Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M219.392 38.0744L247.43 -3.94778L238.28 -10.053L206.168 38.0741L242.279 92.2013L251.43 86.0965L219.392 38.0744Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M55.2809 38.0999L90.418 -4.61917L84.6257 -9.38348L45.6136 38.0467L88.5996 91.498L94.4441 86.7978L55.2809 38.0999Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M197.095 38.0802L226.413 -4.44519L219.003 -9.55353L186.171 38.0703L222.998 91.6974L230.417 86.6025L197.095 38.0802Z' fill='%23B1ED40'/%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M38.81 38.098L74.0764 -4.77818L68.6702 -9.22486L29.7873 38.0482L72.6459 91.3412L78.1007 86.9543L38.81 38.098Z' fill='%23B1ED40'/%3E%3C/svg%3E%0A"), linear-gradient(92.8deg, #A5DE3C 10.06%, #9ED437 97.89%)`,
          mask: `url("data:image/svg+xml,%3Csvg width='430' height='120' viewBox='22 0 430 120' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M22 16C22 7.16344 29.1634 0 38 0H129.5H175.016C180.183 0 185.032 2.49545 188.036 6.70019L188.464 7.29981C191.468 11.5045 196.311 14 201.478 14C210.723 14 225.887 14 237 14C248.113 14 263.277 14 272.522 14C277.689 14 282.532 11.5045 285.536 7.29981L285.964 6.70019C288.968 2.49545 293.817 0 298.984 0H344.5H436C444.837 0 452 7.16345 452 16V120H22V16Z' fill='%23B1ED40'/%3E%3Cellipse cx='16' cy='57.1414' rx='16' ry='19.0476' fill='%23101010'/%3E%3Cellipse cx='458' cy='57.1414' rx='16' ry='19.0476' fill='%23101010'/%3E%3C/svg%3E"), 
        url("data:image/svg+xml,%3Csvg width='430' height='120' viewBox='22 16 430 120' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse cx='16' cy='57.1414' rx='16' ry='19.0476' fill='%23101010'/%3E%3Cellipse cx='458' cy='57.1414' rx='16' ry='19.0476' fill='%23101010'/%3E%3C/svg%3E")`,
          maskSize: "100% auto",
          maskRepeat: "no-repeat",
          maskComposite: "subtract",
          backgroundColor: `#232528`,
          backgroundPosition: "100% 50%",
          backgroundSize: "auto 100%",
          boxShadow: `0px 24px 80px rgba(0, 0, 0, 0.3)`,
        }}
      ></div>

      <div
        className={`absolute w-[56px] h-[6px] rounded-full top-0 left-0 right-0 mx-auto pointer-events-none transition-all duration-200 group-hover:w-[64px] group-active:top-[2px] ${
          slip.slipOpen ? "bg-surface-600" : "bg-secondary-400"
        }`}
      ></div>
      <div className="relative flex items-center gap-1">
        <div
          className={`font-bold text-headline-bold uppercase transition-colors ${
            slip.slipOpen ? "text-white" : "text-dark"
          }`}
        >
          {slip.slipState === "checkout" ? "Pick Checkout" : "Pick Slip"}
        </div>
        {slip.slipState !== "checkout" && (
          <div
            className={`pick-slip-number flex justify-center items-center rounded-lg w-auto min-w-6 h-6 px-2 text-title-bold transition-all ${
              slip.slipOpen
                ? "bg-primary-500 text-secondary-300"
                : "bg-dark text-white"
            } ${displayedNumber > 0 ? "opacity-100" : "opacity-0"}
              `}
          >
            {displayedNumber}
          </div>
        )}
      </div>
      <Dialog.Close
        onClick={() =>
          slip.slipOpen ? slip.removeCheckoutPick() : slip.setSlipOpen(true)
        }
      >
        <div className="relative">
          <div
            className={`flex items-center gap-1 text-body-sm-medium text-secondary-100 uppercase rounded-[100px]  px-3 py-2 transition-colors ${
              slip.slipOpen
                ? "text-surface-500 bg-[rgba(255,255,255,0.15)] hover:bg-[rgba(255,255,255,0.30)] active:bg-[rgba(255,255,255,0.40)]"
                : "text-secondary-100 bg-[rgba(0,0,0,0.07)] hover:bg-[rgba(0,0,0,0.15)] active:bg-[rgba(0,0,0,0.20)]"
            }`}
          >
            {slip.slipOpen ? (
              <div>
                {slip.slipState === "checkout" && !slip.justPurchasedPicks
                  ? "Cancel"
                  : slip.slipState === "checkout" && slip.justPurchasedPicks
                  ? "Close"
                  : "Hide"}
                <Icon name="caret-down" className="fill-secondary-500" />
              </div>
            ) : (
              <div>
                Open <Icon name="caret-up" className="fill-secondary-100" />
              </div>
            )}
          </div>
        </div>
      </Dialog.Close>
    </div>
  );
}
